import type { FC } from 'react';

import { cn } from '@/utils/cn.util';

interface PaginationProps {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  /** Max number of page-number buttons rendered (excluding prev/next). Default 5. */
  visibleWindow?: number;
}

/**
 * Returns a windowed page list ending at totalPages and centered around page when possible.
 * For totalPages <= visibleWindow returns every page; otherwise pads with the current window.
 */
const buildWindow = (page: number, totalPages: number, visibleWindow: number): number[] => {
  if (totalPages <= visibleWindow) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const half = Math.floor(visibleWindow / 2);
  let start = Math.max(1, page - half);
  const end = Math.min(totalPages, start + visibleWindow - 1);
  start = Math.max(1, end - visibleWindow + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

const btnBase =
  'rounded-md border border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-3 py-1.5 text-[11px] font-semibold text-[var(--color-ink-muted)] transition-colors hover:border-brand-500 hover:bg-brand-50 hover:text-brand-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-[var(--color-divider)] disabled:hover:bg-[var(--color-surface-muted)] disabled:hover:text-[var(--color-ink-muted)]';

const Pagination: FC<PaginationProps> = ({ page, totalPages, onChange, visibleWindow = 5 }) => {
  const pages = buildWindow(page, totalPages, visibleWindow);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button
        type="button"
        className={btnBase}
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
      >
        ← Prev
      </button>

      {pages.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          className={cn(
            btnBase,
            p === page && 'border-brand-500 bg-brand-500 text-white hover:bg-brand-500 hover:text-white',
          )}
        >
          {p}
        </button>
      ))}

      <button
        type="button"
        className={btnBase}
        onClick={() => onChange(page + 1)}
        disabled={page >= totalPages}
      >
        Next →
      </button>
    </div>
  );
};

export default Pagination;
